import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import React from 'react';
import { homeStyles } from '..';

import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { useWindowWidth } from '@react-hook/window-size';
import { getCallStatus } from '../call-status';
import { ConferenceCall, conferenceCalls } from '../mobile-live-bar';
import { ShareCallButton } from '../share-call-button';
// useInterval from
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
type IntervalFunction = () => unknown | void;
function useInterval(callback: IntervalFunction, delay: number) {
  const savedCallback = React.useRef<IntervalFunction | null>(null);

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      if (savedCallback.current !== null) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function MediaCalls(props: { videoSRC: string; isPlaylist: boolean }) {
  // Hooks
  const width = useWindowWidth();
  // State
  const [isLive, setIsLive] = React.useState(0);
  const [showShort, setShowShort] = React.useState(false);

  React.useEffect(() => {
    if (props.videoSRC) {
      setShowShort(true);
    }
  }, [props.videoSRC]);

  React.useEffect(() => {
    setIsLive(getCallStatus());
    // ignore 'getStatus' missing dependency array warning
    // eslint-disable-next-line
  }, []);

  // 10 second delay between intervals
  const delay = 10 * 1000;
  useInterval(() => {
    setIsLive(getCallStatus());
  }, delay);

  const call =
    conferenceCalls.find((i) => i.day === isLive) || conferenceCalls[3];
  const isDefault = call.day === 0;

  const featuredYTShort = (
    <>
      <Box
        sx={{
          maxWidth: 250,
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          aspectRatio: '9 / 16',
          position: 'relative',
        }}
      >
        <Skeleton variant="rectangular" height="100%" width="100%" />

        {/* No URL yet (e.g. refetching for an impersonated agent): an empty
            src would load this page inside the frame */}
        {props.videoSRC ? (
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            src={`${props.videoSRC}${
              props.isPlaylist ? '&' : '?'
            }autoplay=1&mute=1`}
            title="Get Comfortable Being Uncomfortable."
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture;"
            allowFullScreen
            loading="eager"
          />
        ) : null}
      </Box>

      <Button
        aria-label={call.name}
        fullWidth
        size="small"
        sx={{ ...homeStyles.button, mt: 1 }}
        onClick={() => setShowShort(false)}
      >
        Hide
      </Button>
    </>
  );

  function FeaturedCall() {
    return (
      <Box
        sx={{
          ...homeStyles.box,
          maxWidth: 250,
          width: '100%',
          height: '100%',
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          boxShadow: isDefault
            ? `0px 2px 5px  #000000`
            : '0px 0px 10px 5px #CE0E2D',
          p: 1,
        }}
      >
        <Box
          p={1}
          sx={{ fontWeight: 'bolder', fontSize: !isDefault ? 18 : 'inherit' }}
        >
          {isDefault ? call.name : 'LIVE NOW'}
        </Box>

        <Box
          component="img"
          src={call.logo}
          alt={call.name}
          sx={{
            maxWidth: { xs: 165, lg: 200 },
            marginX: 'auto',
          }}
        />

        <Stack spacing={1}>
          <Stack spacing={1} direction="row" alignItems="center">
            <Button
              aria-label={call.name}
              fullWidth
              size="small"
              sx={{ ...homeStyles.button }}
              href={call.youtube || ''}
              target="_blank"
              rel="noopener noreferrer"
            >
              {isDefault ? 'Subscribe' : 'Watch Live'}
            </Button>

            <ShareCallButton call={call} />
          </Stack>

          {props.videoSRC && isDefault ? (
            <Button
              aria-label="watch short"
              fullWidth
              size="small"
              sx={{ ...homeStyles.button }}
              onClick={() => setShowShort(true)}
            >
              Watch Latest Video
            </Button>
          ) : null}
        </Stack>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-evenly"
      spacing={3}
      width="100%"
      height="100%"
    >
      <Box flex={1}>
        <Stack
          spacing={isLive !== 0 ? 2 : 1}
          justifyContent="space-between"
          alignItems="stretch"
        >
          {conferenceCalls.map((i: ConferenceCall) => {
            if (i.day !== 0 && i.day !== isLive) {
              return (
                <Box
                  key={i.day}
                  px={2}
                  sx={{
                    ...homeStyles.box,
                    minHeight: isLive !== 0 ? 100 : 80,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    height: '100%',
                  }}
                >
                  <img
                    src={i.logo}
                    alt={i.name}
                    style={{
                      maxWidth: i.maxWidth,
                      marginBottom: i.day === 4 ? -5 : 0,
                    }}
                  />
                  <Box
                    sx={{
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      fontFamily: 'Roboto',
                    }}
                  >
                    {width > 1200 ? i.dateDay : i.dateDay.slice(0, 3)}{' '}
                    {i.dateTime}
                  </Box>
                </Box>
              );
            } else {
              return null;
            }
          })}
        </Stack>
      </Box>
      <Box
        flex={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            perspective: 1000,
            width: '100%',
            maxWidth: 250,
            height: 255,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform:
                showShort && isDefault ? 'rotateY(0deg)' : 'rotateY(180deg)',
            }}
          >
            {showShort && isDefault ? (
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                }}
              >
                {featuredYTShort}
              </Box>
            ) : null}

            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <FeaturedCall />
            </Box>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
